import React from 'react';
import { versionedApiClient, ApiVersion } from '../api/versionedClient';
import { CLIENT_ENDPOINTS, WORKFLOW_ENDPOINTS } from '../api/endpointMap';
import { DatabaseClient } from '../services/api/workflowService';

// Define an interface for missing endpoint tracking
interface MissingEndpoint {
  endpoint: string;
  version: ApiVersion;
  timestamp: string;
}

// Extend the window object to include our missing endpoints tracker
declare global {
  interface Window {
    missingDbEndpoints?: MissingEndpoint[];
    loggedErrors?: Set<string>; // Track which errors we've already logged
  }
}

// Initialize the missing endpoints tracker and error logging set
if (typeof window !== 'undefined') {
  window.missingDbEndpoints = window.missingDbEndpoints || [];
  window.loggedErrors = window.loggedErrors || new Set();
}

// Helper function to track missing endpoints
const trackMissingEndpoint = (endpoint: any, version: ApiVersion) => {
  if (typeof window !== 'undefined') {
    window.missingDbEndpoints = window.missingDbEndpoints || [];
    
    // Get the endpoint string based on the current version
    const endpointString = typeof endpoint === 'string' 
      ? endpoint 
      : endpoint[version] || JSON.stringify(endpoint);
    
    // Create a unique key for this endpoint+version
    const errorKey = `${version}:${endpointString}`;
    
    // Only log each missing endpoint once to console
    if (!window.loggedErrors?.has(errorKey)) {
      window.loggedErrors?.add(errorKey);
      console.warn(`MISSING ${version.toUpperCase()} ENDPOINT: ${endpointString} is not implemented`);
    }
    
    // Check if we already added this endpoint to our list
    const alreadyTracked = window.missingDbEndpoints.some(
      e => e.endpoint === endpointString && e.version === version
    );
    
    // Only add if not already in the list
    if (!alreadyTracked) {
      window.missingDbEndpoints.push({
        endpoint: endpointString,
        version,
        timestamp: new Date().toISOString()
      });
    }
  }
};

interface VersionedApiHookResult {
  // Version management
  apiVersion: ApiVersion;
  setApiVersion: (version: ApiVersion) => void;
  
  // Common naming for backward compatibility
  isUsingDatabaseApproach: boolean;
  
  // Client operations
  getClients: () => Promise<string[] | DatabaseClient[]>;
  getClientById: (clientId: string | number) => Promise<any>;
  
  // Workflow operations
  getWorkflows: (clientId: string | number) => Promise<any[]>;
  getWorkflow: (workflowId: string | number) => Promise<any>;
  
  // Helper functions
  clientIdToString: (clientId: string | number) => string;
  getWorkflowIdParam: (workflow: any) => string;
}

/**
 * Hook for using the versioned API client
 */
export default function useVersionedApi(): VersionedApiHookResult {
  // Always use 'db' API version
  const apiVersion: ApiVersion = 'db';
  
  // Set the API version - no-op since we always use 'db'
  const setApiVersion = (version: ApiVersion) => {
    // No-op: API version is fixed to 'db' mode
  };
  
  // Get clients based on current API version
  const getClients = async () => {
    try {
      const response = await versionedApiClient.get(
        CLIENT_ENDPOINTS.LIST_CLIENTS,
        {}
      );
      
      return response.data;
    } catch (error: any) {
      // Provide detailed error information for DB/v2 endpoints to help with implementation
      if ((apiVersion === 'db' || apiVersion === 'v2') && error.response && error.response.status === 404) {
        trackMissingEndpoint(CLIENT_ENDPOINTS.LIST_CLIENTS, apiVersion);
      }
      
      console.error('Error fetching clients:', error);
      return [];
    }
  };
  
  // Get a client by ID
  const getClientById = async (clientId: string | number) => {
    try {
      const response = await versionedApiClient.get(
        CLIENT_ENDPOINTS.GET_CLIENT,
        { id: clientId }
      );
      
      return response.data;
    } catch (error: any) {
      if ((apiVersion === 'db' || apiVersion === 'v2') && error.response && error.response.status === 404) {
        trackMissingEndpoint(CLIENT_ENDPOINTS.GET_CLIENT, apiVersion);
      }
      
      console.error(`Error fetching client ${clientId}:`, error);
      throw error;
    }
  };
  
  // Get workflows for a client
  const getWorkflows = async (clientId: string | number) => {
    try {
      const response = await versionedApiClient.get(
        WORKFLOW_ENDPOINTS.LIST_WORKFLOWS,
        { client_id: clientId },
        { params: apiVersion === 'v1' ? { client_id: clientId } : {} }
      );
      
      return apiVersion === 'v1' 
        ? response.data || []
        : (response.data.workflows || []);
    } catch (error: any) {
      if ((apiVersion === 'db' || apiVersion === 'v2') && error.response && error.response.status === 404) {
        trackMissingEndpoint(WORKFLOW_ENDPOINTS.LIST_WORKFLOWS, apiVersion);
      }
      
      console.error(`Error fetching workflows for client ${clientId}:`, error);
      return [];
    }
  };
  
  // Get a workflow by ID
  const getWorkflow = async (workflowId: string | number) => {
    try {
      const response = await versionedApiClient.get(
        WORKFLOW_ENDPOINTS.GET_WORKFLOW,
        { id: workflowId }
      );
      
      return response.data;
    } catch (error: any) {
      if ((apiVersion === 'db' || apiVersion === 'v2') && error.response && error.response.status === 404) {
        trackMissingEndpoint(WORKFLOW_ENDPOINTS.GET_WORKFLOW, apiVersion);
      }
      
      console.error(`Error fetching workflow ${workflowId}:`, error);
      throw error;
    }
  };
  
  // Helper to convert client ID to string
  const clientIdToString = (clientId: string | number): string => {
    if (typeof clientId === 'string') {
      return clientId;
    }
    
    return clientId.toString();
  };
  
  // Helper to get correct workflow ID parameter
  const getWorkflowIdParam = (workflow: any): string => {
    if (apiVersion === 'v1') {
      return workflow.id;
    } else {
      return (workflow.external_id || workflow.workflow_id).toString();
    }
  };
  
  return {
    apiVersion,
    setApiVersion,
    isUsingDatabaseApproach: apiVersion === 'db', // For backward compatibility
    getClients,
    getClientById,
    getWorkflows,
    getWorkflow,
    clientIdToString,
    getWorkflowIdParam
  };
} 